import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useActivePage, useNotifications } from '@toolpad/core';
import { format, parse } from 'date-fns';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { Card, CardContent, Stack, Typography } from '@mui/material';

import { QueryRenderer } from '@barboxx/admin-components';
import Api from '@barboxx/api';
import { Customer } from '@barboxx/models';
import { USD } from '@barboxx/utils';

import { RootContainer } from '../../components/nav/RootContainer';
import { CUSTOMER_SCHEMA, CustomerForm } from '../components/CustomerForm';

interface CustomerDetailsCardProps {
    customer: Customer.WithSales;
    onSuccess: () => void;
}

const CustomerDetailsCard = ({
    customer,
    onSuccess,
}: CustomerDetailsCardProps) => {
    const notifications = useNotifications();

    const formContext = useForm<Customer.UpdateRequest>({
        resolver: yupResolver(CUSTOMER_SCHEMA),
        defaultValues: {
            ...customer,
            dob: parse(
                customer.dob,
                'yyyy-MM-dd',
                new Date()
            ) as unknown as string,
        },
    });

    const mutateCustomer = useMutation({
        mutationKey: ['customer', customer.id],
        mutationFn: (data: Customer.UpdateRequest) =>
            Api.Customers.updateCustomer(Number(customer.id), data),
    });

    const onSubmit = async (values: Customer.UpdateRequest) =>
        mutateCustomer
            .mutateAsync({
                ...values,
                dob: format(new Date(values.dob as string), 'yyyy-MM-dd'),
            })
            .then(({ data }) => {
                notifications.show('Updated customer!', {
                    severity: 'success',
                });

                formContext.reset(data);
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error Updating customer!', {
                    severity: 'error',
                });
            });

    return (
        <Stack gap={2}>
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Customer Spend Summary
                    </Typography>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        gap={2}
                        justifyContent="space-between"
                    >
                        <Stack>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Total Spend
                            </Typography>
                            <Typography variant="h6">
                                {USD(customer.totalSales || 0).toString()}
                            </Typography>
                        </Stack>
                        <Stack>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Total Tips
                            </Typography>
                            <Typography variant="h6">
                                {USD(customer.totalTips || 0).toString()}
                            </Typography>
                        </Stack>
                        <Stack>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Total Fees
                            </Typography>
                            <Typography variant="h6">
                                {USD(customer.totalFees || 0).toString()}
                            </Typography>
                        </Stack>
                        <Stack>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Total Orders
                            </Typography>
                            <Typography variant="h6">
                                {customer.totalOrders || 0}
                            </Typography>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            <Card>
                <CardContent>
                    <CustomerForm
                        formContext={formContext}
                        onSubmit={onSubmit}
                        id={customer.id}
                    />
                </CardContent>
            </Card>
        </Stack>
    );
};

export function CustomerDetails() {
    const { id } = useParams();

    const activePage = useActivePage();

    const breadcrumbs = [
        ...(activePage?.breadcrumbs ?? []),
        { title: 'Details', path: `${activePage?.path}/${id}` },
    ];

    const customerQueryResults = useQuery({
        queryKey: ['customer', id],
        queryFn: () => Api.Customers.getCustomer(Number(id)),
    });

    return (
        <RootContainer breadcrumbs={breadcrumbs} title="Customer Details">
            <QueryRenderer results={customerQueryResults}>
                {(data, actions) => {
                    return (
                        <CustomerDetailsCard
                            customer={data}
                            onSuccess={actions.refetch}
                        />
                    );
                }}
            </QueryRenderer>
        </RootContainer>
    );
}
